/** Known LaunchDarkly flag keys, centralised so the string literal lives in
 *  one place across the codebase. Keys are kebab-case to match the LD project
 *  convention (cf. `userpilot-enabled`). */

/** Gates the intake submission-logic runtime (requester page navigator + the
 *  submit rework it builds toward). Default **off** in all environments and
 *  treated as off when absent, so the incremental epic can land slice by slice
 *  without changing the live single-shot requester flow until it is
 *  feature-complete. Epic #2698, slice S1 #2705. */
export const INTAKE_SUBMISSION_LOGIC_FLAG = "intake-submission-logic";

/** Gates the Reporting Teammate (the reporting agent): the "Create with Reporting
 *  Teammate" entry and the generated rows on the Reports page (NG-1588; the entry
 *  was "Create with AI" on Dashboards until then) and the chat route. Created
 *  manually in the LD UI; defaults **off** and is treated as off when the flag is
 *  absent. FE work: #2448 (Refs #2160, #2410). */
export const REPORTING_AGENT_FLAG = "reporting-agent";

/** Gates the experimental team permission graph: the admin-only "Team access"
 *  entry on the team settings Members tab and its modal. Created manually in the
 *  LD UI; defaults **off** and is treated as off when the flag is absent, so it
 *  can merge dark and be turned on per-environment for a product review. */
export const EXPERIMENTAL_TEAMS_GRAPH_FLAG = "experimental-teams-graph";

/** Gates the Recurring Events epic (NG-29): the recurrence editor in the Create/
 *  Edit Event modal, the "edit this / following / all" scope dialog, and the
 *  recurrence marker across the event views. Created manually in the LD UI;
 *  defaults **off** and is treated as off when absent, so the epic lands dark and
 *  turns on per-environment. Pairs with the backend ``RECURRING_EVENTS_ENABLED``
 *  kill-switch. FE work: NG-395 / NG-396. */
export const RECURRING_EVENTS_FLAG = "recurring-events";

/** Gates every goals surface: the sidebar Strategy section and its Goals item,
 *  the `/:slug/goals` management page, and goals as a project field (the picker
 *  in project settings → General, the Goals row in the team project side panel,
 *  and the reference prefetch the project overview loader makes for it).
 *
 *  Goals reached every environment as soon as NG-60/NG-67 landed, but edit and
 *  delete are still open, so the flag is what decides where a half-built surface
 *  is visible: **on** in local/development/staging, **off** in production, and
 *  treated as off when absent. NG-153.
 *
 *  Reports read this flag too (NG-622): the report builder's column picker is
 *  populated from the BACKEND field catalog, which knows nothing about
 *  LaunchDarkly, so the Goals columns are filtered client-side here rather than
 *  being absent from the response. Who may *see* goal data is a separate,
 *  server-side question answered by `goal.read`. */
export const GOALS_FLAG = "goals";

/** Gates the program metrics surfaces: the Progress / Health / Effort columns on
 *  the workspace Programs list, and the KPI band (Projects Overdue, Program
 *  Progress, Hours Logged + the status/health/effort donuts) on the program
 *  overview page. NG-2229 rides it too: sortable program tables, the alignment
 *  strip and the Details sheet.
 *
 *  Only the *read* surfaces are gated. The rollup table, its staleness marking
 *  and the 5-minute drain keep running with the flag off, so the numbers are
 *  already correct the moment it is switched on rather than starting at an
 *  em-dash for a drain cycle; the `metrics` block on the program serializer is
 *  additive and inert when nothing renders it.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  the flag is absent. NG-285 (epic), NG-289 / NG-290 (the gated surfaces). */
export const PROGRAM_METRICS_FLAG = "program-metrics";

/** Gates the program overview's **Linked requests** section (NG-1630): the table
 *  of requests that picked this program at intake, and its link / unlink
 *  controls.
 *
 *  Only the read surface is gated. The endpoint behind it
 *  (`/programs/<id>/requests/`) is ungated and RBAC-guarded — it rides
 *  `program.read` and fences its rows to the requests the caller may read, so a
 *  direct API caller is unaffected by the flag. The demo seeder writes the link
 *  rows with the flag off too, so the table is already populated the moment it
 *  is switched on rather than starting empty.
 *
 *  Defaults **off** and is treated as off when the flag is absent, so the
 *  section lands dark and turns on per-environment. NG-1630 (parent NG-1568). */
export const PROGRAM_LINKED_REQUESTS_FLAG = "program-linked-requests";

/** Gates the Skills CSV bulk-import surface: the "Import" button on the
 *  workspace settings → Skills page and the dialog it opens (the first consumer
 *  of the reusable CsvImporter, NG-645). Only the UI entry point is gated — the
 *  backend `skills/bulk-import/` endpoint is ungated and RBAC-guarded, so a
 *  direct API caller is unaffected by the flag. Created manually in the LD UI;
 *  defaults **off** and is treated as off when absent, so the consumer lands
 *  dark and turns on per-environment. NG-661 (epic NG-659). */
export const SKILLS_CSV_IMPORT_FLAG = "skills-csv-import";
/** Gates the Request CSV bulk-import surface: the "Import requests" button on the
 *  workspace Requests list page and the wizard it opens (pick a target form →
 *  upload → map → review → import, the second consumer of the reusable
 *  CsvImporter). Only the UI entry point is gated — the backend
 *  `intake/forms/<id>/import/` endpoint is ungated and RBAC-guarded, so a direct
 *  API caller is unaffected by the flag. Defaults **off** and is treated as off
 *  when absent, so the consumer lands dark and turns on per-environment.
 *  Permissions + rollout are owned by NG-786. NG-784 (epic NG-779). */
export const REQUESTS_CSV_IMPORT_FLAG = "requests-csv-import";
/** Gates the Project CSV bulk-import surface: the "Import projects" button on the
 *  workspace Projects list page and the wizard it opens (upload → map columns →
 *  map values → review, the fourth consumer of the reusable CsvImporter). Only
 *  the UI entry point is gated — the backend
 *  `projects/import-definition/` endpoint is ungated and RBAC-guarded
 *  (`project.create` OR `workspace.edit`), so a direct API caller is unaffected
 *  by the flag. Defaults **off** and is treated as off when absent, so the
 *  consumer lands dark and turns on per-environment; the commit half of the
 *  wizard ships with NG-1673. NG-1674 (epic NG-1670). */
export const PROJECTS_CSV_IMPORT_FLAG = "projects-csv-import";
/** Gates the custom-process editor in Settings › Processes: the "New process"
 *  toolbar button and the Edit / Delete row actions on a custom process. With
 *  the flag off the page is exactly what it was — a read-only list, system
 *  processes badged and actionless — so the editor lands dark and turns on per
 *  environment. Created manually in the LD UI on 2026-08-26; defaults **off**
 *  everywhere and is treated as off when the flag is absent. NG-634, epic
 *  NG-530. */
export const CUSTOM_PROCESSES_FLAG = "custom-processes";

/** Gates the Cornerstone (CSOD) LMS integration, and only that.
 *
 *  NG-942 S1.5 said Cornerstone is never visible while 360Learning is off,
 *  because 360Learning built the connection, catalog, link / unlink and results
 *  surfaces Cornerstone reuses. That rule is now satisfied for good: 360Learning
 *  shipped to everyone and its flag was retired (NG-2072), so there is nothing
 *  left to order this key against. Cornerstone is always named in the catalog
 *  and connects only while this key is on.
 *
 *  Read it through `connector-catalog.ts`, where it is Cornerstone's
 *  `connectFlag`, rather than at a call site: `connectorsFor` and
 *  `availabilityOf` are what turn it into Available or Planned.
 *
 *  Created in LaunchDarkly on 2026-09-14 and treated as off when absent. NG-941
 *  (epic), NG-942 (this flag).
 *
 *  What it gates, since NG-2242: whether Cornerstone is Available in the Integration
 *  Library, which is what opens its connector page (the route refuses it while
 *  Planned), its Connect form, and its row on Installed. With it on, the LMS sentences
 *  with no connection in hand (the Delivery and picker empty states, the
 *  recommended-content copy) stop naming 360Learning, because they cannot tell which
 *  vendor they are about (`workspaceLmsVendor` in `lib/integrations/lms-vendors.ts`).
 *  The backend connects Cornerstone only where `CORNERSTONE_API_SCOPES` is set, and
 *  refuses the form otherwise, so an environment needs both. */
export const LMS_INTEGRATION_CORNERSTONE_FLAG = "lms-integration-cornerstone";

/** Gates the private draft-requests surface in the requester fill flow
 *  (NG-702, epic NG-657): the "Save draft" action while filling a form, the
 *  resume-from-draft path (`?draft=` on the fill route), routing submit of a
 *  resumed draft through the draft-submit endpoint, and the "Your drafts"
 *  section on the request-type picker. The backend draft endpoints are ungated
 *  and RBAC-guarded — the flag only decides where the UI surfaces. Defaults
 *  **off** and is treated as off when absent, so the lane lands dark and turns
 *  on per environment. */
export const PRIVATE_DRAFT_REQUESTS_FLAG = "private-draft-requests";
/** Decides how a NEW request submission is created (NG-703 / NG-1010): with the
 *  flag on, the requester flow sends `completes_requester_pages: "first"`, so the
 *  backend halts the run at the next unconfirmed requester page and persists
 *  that mode on the run; off, it sends `"all"`, today's walk past every page.
 *  Deliberately the flag's ONLY consumer: the "Complete this step" button on the
 *  pending page renders from the server's `can_complete_step` verdict (derived
 *  from the persisted mode), never from this flag — the assignee may evaluate
 *  it differently from the submitter during a targeted rollout, and turning it
 *  off must not strand a request already halted waiting for the action. There is
 *  no backend switch to order against it. Defaults **off** and is treated as off
 *  when absent. NG-703 (epic NG-657). */
export const WORKFLOW_STEP_COMPLETION_FLAG = "workflow-step-completion";
/** Gates the "Create project" button on a request's Linked Projects tab
 *  (NG-701): the Request Owner's entry into the standard project-creation
 *  dialog, pre-linked to the request. Only the button is gated — the tab, the
 *  table and the backend convert endpoints are ungated and RBAC-guarded, and
 *  the `can_create_project` server verdict (owner-only) still decides who could
 *  ever see it. Defaults **off** and is treated as off when absent, so the
 *  slice lands dark and turns on per environment once the epic's lanes are
 *  reviewed together. NG-701 (epic NG-657). */
export const REQUEST_LINKED_PROJECTS_CREATE_FLAG = "request-linked-projects-create";
/** Gates the persisted table sort and column filters (NG-517): the `sorting` and
 *  `columnFilters` slices of the per-browser table preferences, on both the
 *  hook path (`useTableSorting` / `useTableColumnFilters`, which every
 *  client-sorted list surface takes) and the store path (the ProjectStore,
 *  IntakeStore and ReportStore reactions that persist their server-sorted
 *  lists).
 *
 *  Only those two slices are gated. `columnOrder`, `columnVisibility` and
 *  `columnSizing` (NG-451) keep persisting for everyone — they predate this work
 *  and are not part of it.
 *
 *  With the flag off a table sorts and filters exactly as it did before NG-517:
 *  the controls still work, the choice just does not survive a reload, and
 *  nothing is read from or written to localStorage for those two slices. So the
 *  work lands dark and turns on per environment. Created in the LD project
 *  `next-gen` on 2026-09-02, **off** in all four environments (local,
 *  development, staging, production), and treated as off when absent. NG-517. */
export const PERSISTED_TABLE_PREFS_FLAG = "persisted-table-prefs";

/** Gates the whole Microsoft Teams notification-channel surface (epic NG-886): the
 *  workspace settings → Integrations entry that consents the tenant, the
 *  per-person "connect my Teams" control, and the category picker that narrows
 *  which notifications go out.
 *
 *  Off is the only safe default here, because the thing on the other side of the
 *  flag is a message in somebody's Teams chat: a half-wired channel does not
 *  degrade quietly the way a hidden page does, it pushes cards. So the flag is
 *  **off** everywhere and treated as off when absent, and the backend substrate
 *  (NG-952) lands first, dark and unreachable — no connection row exists, so the
 *  emit path finds nothing to deliver to whatever the flag says. NG-952 (this
 *  slice), epic NG-886.
 *
 *  Read by the Integration Library route and its Microsoft Teams card (NG-1096) —
 *  the first call sites. The knip dark-flag exemption this export carried while the
 *  key was registered a PR ahead of its UI is therefore deleted: left behind, it
 *  would let a genuinely dead flag key sit in this file unnoticed. */
export const MS_TEAMS_INTEGRATION_FLAG = "ms-teams-integration";

/** Widens the request status control to every workspace status (NG-1089, epic
 *  NG-1088). Off, the shared status select (request detail header + the Requests
 *  table's inline cell) and the board's drag targets offer only the statuses
 *  some step of the form's published Workflow Builder workflow assigns
 *  (`workflow_status_ids`), which locks out Waitlisted, Declined and custom
 *  statuses on happy-path workflows. On, that configured set is no longer fed to
 *  `classifyTransitions`, so only the legacy per-status transition rules (movers,
 *  required approvals) can still block or lock an option; the reason, approval
 *  and blocked dialogs, the read-only pill and the per-row `can_change_status`
 *  verdict are untouched. Ordering, not compilation: the API gate has to be gone
 *  first (NG-1128) or an out-of-set pick 400s. Created manually in the LD UI;
 *  defaults **off** and is treated as off when absent, so the widening lands
 *  dark and turns on per environment. */
export const REQUEST_STATUS_FULL_LIST_FLAG = "request-status-full-list";

/** Gates the team-hierarchy editing surfaces (epic NG-1145): the Parent team row
 *  on a team's Settings → General tab, and — as the epic's later slices land —
 *  the picker that changes it (NG-1147) and its confirmation dialog.
 *
 *  Off is the right default because a re-parent is not a cosmetic edit: it moves
 *  a whole subtree's materialized path, recomputes inherited ghost members up and
 *  down the new ancestor chain, and changes who can see the moved team's data in
 *  reporting. The epic arrives in slices, so the flag is what keeps a partly-built
 *  hierarchy editor out of everyone's settings page until the whole of NG-1145 can
 *  be reviewed together.
 *
 *  Only the UI is gated. `PATCH /teams/<id>/` has accepted `parent` since #3054 and
 *  stays ungated and RBAC-guarded, so a direct API caller is unaffected by the flag.
 *  Defaults **off** and is treated as off when absent. NG-1146 (this slice). */
export const TEAM_HIERARCHY_EDITING_FLAG = "team-hierarchy-editing";

/** Gates the dashboard-sharing surfaces (epic NG-1110) as its slices land: the
 *  Share button and sharing modal on a dashboard, the shared-with-me entries and
 *  their visual distinction on the dashboards list, the viewer (read-only)
 *  dashboard chrome, the owner's saved filter view carried to sharees, and the
 *  Duplicate action.
 *
 *  Off is the right default because the epic rewires who can open a dashboard at
 *  all — a half-built share model must not leak a dashboard (or its
 *  workspace-scoped widget data) to recipients before the per-viewer data-rights
 *  scoping lands. The epic arrives in slices behind this one key and turns on
 *  per environment only when the whole of NG-1110 passes QA; there is no
 *  feature-specific early access.
 *
 *  Created manually in the LD UI; defaults **off** in all four environments and
 *  is treated as off when absent. NG-1110 (epic). First consumers: the Share
 *  button + modal on the dashboard detail page (NG-1115), the Duplicate action
 *  in the detail action menu (NG-1260), and the list page's search, sort and
 *  "Shared" badge (NG-1111). */
export const DASHBOARD_SHARING_FLAG = "dashboard-sharing";

/** Gates every surface of the Bulk Import Users epic (NG-1288) as its slices
 *  land: the import template download on Workspace Settings › Users (NG-1293),
 *  and the import modal, validation and create/update behaviour that follow.
 *
 *  One key for the whole epic rather than one per story, because the slices are
 *  only coherent together — a template describing columns nothing can yet import
 *  is not something to show a customer. Off is the right default for the same
 *  reason the epic is flagged at all: the import writes users, roles and team
 *  membership in bulk, so it stays dark until the whole of NG-1288 passes QA.
 *
 *  Created manually in the LD UI; defaults **off** in all environments and is
 *  treated as off when absent. NG-1288 (epic). */
export const USERS_BULK_IMPORT_FLAG = "users-bulk-import";

/** Gates every customer-facing surface of the Custom System Roles epic
 *  (NG-1233) as its slices land: the System Role vocabulary (NG-1246), the
 *  permission-gated Roles surface (NG-1254), the System Roles table, create
 *  modal and edit page (NG-1255/1256/1259/1257/1271), enforcement following the
 *  toggles (NG-1258), the Members/Access/export changes (NG-1247/1249/1250),
 *  and the removal of the profile Danger Zone's self-deactivation control and
 *  the user menu's Workspace Invitations item (NG-1248).
 *
 *  One key for the whole epic rather than one per story, because the slices are
 *  only coherent together: the rename alone renames a page whose Create button
 *  is still inert, and the create modal alone would ship under the old
 *  vocabulary. Off is the right default because the epic changes what a role
 *  *means* — the backend now derives a member's License Tier from their System
 *  Role — so the surface stays dark until the whole of NG-1233 passes QA and
 *  product picks the moment.
 *
 *  Its first consumer is the vocabulary overlay, so this key needs no
 *  ahead-of-its-call-site exemption tag: `SystemRoleVocabularySync` imports
 *  this key today.
 *
 *  Created manually in the LD UI; defaults **off** in all environments and is
 *  treated as off when absent. NG-1233 (epic), NG-1246 (first slice). */
export const CUSTOM_SYSTEM_ROLES_FLAG = "custom-system-roles";

/** Gates the Rebalance entry points on the Capacity People view: the action on
 *  an over-allocated person's row and the matching action on an over-allocation
 *  risk chip. The proposal dialog and its backend endpoint remain in place, so
 *  turning the flag back on restores the existing workflow without a deploy.
 *  Defaults **off** and is treated as off when absent, so Rebalance stays hidden
 *  while product decides when to make it available again. */
export const CAPACITY_REBALANCE_FLAG = "capacity-rebalance";

/** Gates the Help Center entry in the user menu, the product's only route to
 *  https://help.cognota.com. A link is not a half-built surface, but every
 *  visible change ships dark and product decides when it turns on, so it gets a
 *  flag like anything else people can see. Created manually in the LD UI;
 *  defaults **off** and is treated as off when the flag is absent. NG-1517. */
export const HELP_CENTER_LINK_FLAG = "help-center-link";

/** Gates logging and reading time as hours and minutes (`1h 10m`) rather than as
 *  a decimal number of hours (`1.167h`): the paired inputs in the Log time
 *  dialog, and every surface that renders a logged total.
 *
 *  Nothing changes underneath it — `IssueWorkLog.duration` has always been an
 *  integer number of seconds — so the flag switches presentation only and the
 *  same stored rows read correctly either way. Created manually in the LD UI;
 *  defaults **off** and is treated as off when the flag is absent. NG-1516. */
export const TIME_LOG_HOURS_MINUTES_FLAG = "time-log-hours-minutes";

/** Gates the customer-visible surfaces of the NG-1367 password-policy epic's
 *  account-security work (NG-1368 audit logging, NG-1369 lockout, NG-1371
 *  SSO override, NG-1373 workspace settings): the `login.succeeded`/
 *  `login.failed` rows in the workspace Audit Log (list + CSV export; the
 *  row detail side panel is only reachable by clicking a row, so filtering
 *  the list already keeps it out of reach), the account-locked messaging on
 *  the sign-in error banner, the FR20 "password-login override" section on
 *  Workspace Settings → SSO, and the Workspace Settings → Password policy
 *  tab (NG-1373) — an admin's ability to raise this workspace's password
 *  requirements above the platform floor.
 *
 *  The backend enforcement itself is NOT gated by this flag and never was —
 *  every login is audited, the lockout after 10 failed attempts is always
 *  active, the `PATCH .../sso-override/` endpoint always exists, and a
 *  workspace's configured password policy (min-length, variety, reuse,
 *  expiration) is enforced regardless of whether an admin can currently see
 *  the settings UI that configures it — these are security controls, not a
 *  rollout feature. While the flag is off: the two new audit event types
 *  are excluded from the Audit Log (via `exclude_category=auth` on the
 *  list and export calls) rather than left silently unfilterable, a
 *  locked-out sign-in falls back to the existing generic
 *  RATE_LIMIT_EXCEEDED message instead of a lockout-specific one, the
 *  override section is simply absent from the SSO settings page (an admin
 *  can still reach it via the API or the break-glass management command),
 *  and the Password policy tab is simply absent from Workspace Settings.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off
 *  when the flag is absent. NG-1610, NG-1613, NG-1371, NG-1373. */
export const PASSWORD_POLICY_FLAG = "password-policy";

/** Gates Workspace Settings → Integrations → AI provider: the page where a
 *  workspace admin chooses Cognota-managed or their own LLM provider, enters its
 *  credential and tests the connection (epic NG-1357, WS5 NG-1362). Only the UI
 *  is gated — the `/api/workspaces/<slug>/ai-provider/` endpoints are ungated and
 *  RBAC-guarded (`workspace.aiProvider.manage`), and the reporting agent already
 *  runs on whatever the workspace resolves to. Created manually in the LD UI;
 *  defaults **off** everywhere and is treated as off when the flag is absent. */
export const BYO_AI_PROVIDER_FLAG = "byo-ai-provider";

/** Gates the deferral of the Projects page's reference banks (NG-1566): with
 *  the flag on, the six banks that feed only the table's cells and the filter
 *  popover's facets — members, teams, programs, goals, custom fields and
 *  category fields — are fetched when the table is the active layout or the
 *  filter panel is first opened, instead of on mount whatever the layout. Off,
 *  all six go out on mount exactly as they did before, so a board open still
 *  pays for six responses it never reads.
 *
 *  It gates the filter popover's loading state with them, and has to: deferral
 *  is what opens the gap the placeholders fill. With the flag off the banks are
 *  in flight from mount, so the panel shows **no placeholder at all** — which is
 *  what it does on `main` today, and what makes this dark rather than merely
 *  quieter.
 *
 *  Deliberately NOT gated: the category-values cache added alongside it. That
 *  one stops a refetch of data the page already holds and changes nothing a user
 *  can see, so it is the "internals / perf" case that ships unflagged.
 *
 *  A flag for a perf change because the deferral has one visible consequence —
 *  the filter panel fills in rather than arriving full — and product decides
 *  when that is on. Created in the LD project `next-gen` on 2026-09-22, **off**
 *  in all four environments (local, development, staging, production), and
 *  treated as off when absent. NG-1566. */
export const PROJECTS_DEFERRED_REFERENCE_BANKS_FLAG = "projects-deferred-reference-banks";

/** Gates every surface of the Events Polish epic (NG-1592) as its slices land —
 *  work-item colour coding, the hourly Timeline layouts, Show Archived on the
 *  time-axis views, the rebuilt filter control. Its first is the Timeline's
 *  removal from the workspace Tasks list (NG-1602). The epic key is the durable
 *  pointer; the slice list lives on the epic, where renumbering it is free.
 *
 *  One key for the whole epic rather than one per story, and the epic says so
 *  outright: the slices are only coherent together. A customer migrating from
 *  Classic is promised Events at parity, and half of it — Timeline gone from
 *  Tasks but still hourless on Events, colours settable but not shown — is a
 *  worse page than the one they have now. Off is therefore the right default,
 *  and product decides when it goes on; merged is not released.
 *
 *  Created manually in the LD UI; defaults **off** in all four environments
 *  (local, development, staging, production) and is treated as off when the
 *  flag is absent. NG-1592 (epic), NG-1602 (the slice that registers the key).
 *
 *  **Not every slice of the epic rides this flag, and the test is per change,
 *  not per ticket: does it alter what is on the screen?** The epic carries a
 *  run of timezone *correctness* bugs (NG-1791, NG-1886) whose subject is what
 *  we write to the database, and those ship ungated — the surfaces they fix are
 *  live in production today, so gating them would leave one page giving two
 *  answers about what time something is, and every day dark is another day of
 *  rows we cannot repair afterwards (the author's original zone was never
 *  recorded). Where such a slice also adds something visible — NG-1886's "Times
 *  are in the event's time zone" line — that part alone sits behind this flag.
 *  NG-1792 / NG-1796 change what a surface *displays* and are gated normally.
 *
 *  **Not every surface it gates is an Events surface.** NG-24 sits under this
 *  epic and fixes the month view's week-column headers on the three GANTT views
 *  — Projects, Programs and the Requests Plan Work chart — which read their axis
 *  from the same `buildAxis` the Timeline does. Events itself is deliberately
 *  excluded there: its month view is the calendar grid, which never reads those
 *  cells. So a read of this key inside a Projects or Programs file is expected
 *  rather than a stray import of the wrong flag. */
export const EVENTS_POLISH_FLAG = "events-polish";

/** Gates the login screen's new "SSO unavailable" state (NG-1370, FR26/FR28,
 *  epic NG-1367). Today, an SSO-enabled domain whose identity provider is
 *  broken or unconfigured silently bounces the user back to the email-entry
 *  screen with no visible explanation (the SSO-initiate failure redirects
 *  with `?error=`, a query param the login screen's error banner has never
 *  read — it only reads `?error_code=`). This flag decides which of two
 *  read-only backend facts to render for that case: off, "SSO" (today's
 *  auto-redirect into the same silent failure, pixel-identical to current
 *  behavior); on, "SSO_UNAVAILABLE" (a support-contact banner instead).
 *
 *  No backend behavior is gated — `EmailCheckEndpoint` always reports the
 *  factual live/unavailable state; this flag only decides which one the
 *  frontend acts on. Pre-auth, so it evaluates against an anonymous LD
 *  context rather than a signed-in user's. Created manually in the LD UI;
 *  defaults **off** and is treated as off when the flag is absent. */
export const SSO_LOGIN_ROUTING_FLAG = "sso-login-routing";

/** Gates surfacing the backend's specific rejection message on the SAML/OIDC
 *  provider forms (NG-1927) when NG-1702's single-active-provider rule (FR21)
 *  rejects activating a second identity provider. Off: the existing generic
 *  "Failed to create/update provider" toast for every error, unchanged. On:
 *  that one documented rejection shows its own message ("Another identity
 *  provider is already active for this workspace. Disable it before
 *  activating another."), telling the admin how to remediate instead of
 *  leaving them to guess; every other error still falls back to the generic
 *  toast. Created manually in the LD UI; defaults **off** and is treated as
 *  off when the flag is absent. Do NOT restore the pre-NG-1702 silent
 *  auto-disable behavior regardless of this flag's state — that reject-vs-
 *  auto-switch call was made deliberately on NG-1702. */
export const SSO_PROVIDER_ERROR_DETAIL_FLAG = "sso-provider-error-detail";

/** Gates the customer-visible surfaces of NG-1762 (FR42-FR51, epic NG-1367): the
 *  Workspace Settings → Session timeout tab — an admin's ability to enable an
 *  idle timeout (min 15 minutes) and lower the absolute session ceiling (min 1
 *  hour) below the loose platform defaults — and the explicit "session expired"
 *  banner on the sign-in screen.
 *
 *  A dedicated key rather than folding into {@link PASSWORD_POLICY_FLAG}: that
 *  flag's own docstring scopes it to NG-1368/1369/1371/1373, and this story's
 *  rollout decision is independent of those — the same reasoning
 *  {@link SSO_LOGIN_ROUTING_FLAG} used for its own NG-1367 slice.
 *
 *  The backend enforcement itself is NOT gated by this flag and never was: the
 *  30-day absolute ceiling (and any workspace-configured idle timeout or
 *  tighter ceiling) is enforced by `SessionTimeoutMiddleware` regardless of
 *  whether an admin can currently see the settings UI that configures it —
 *  this is a security control, not a rollout feature. While the flag is off:
 *  the Session timeout tab is simply absent from Workspace Settings (an admin
 *  can still reach the underlying dials via the API), and a session-expiry 401
 *  falls back to today's silent redirect with no banner — `authErrorHandler`
 *  returns `null` for `SESSION_EXPIRED` specifically until the flag is on.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  the flag is absent. NG-1762 (epic NG-1367). */
export const SESSION_TIMEOUT_FLAG = "session-timeout";

/** Gates the read-only state on a record the viewer can open but not edit
 *  (NG-1872): a collapsed "You can read this … but not change it." banner with a
 *  "Why?" disclosure naming the viewer's System Role, over fields that render as
 *  disabled controls rather than as plain text or live inputs. One key for the
 *  epic's four surfaces (Requests NG-1875, Projects NG-1876, Programs NG-1877,
 *  Work items NG-1878), because the PRD's point is one consistent pattern: half
 *  the surfaces explaining themselves and half not is the confusion it removes.
 *
 *  It ships alongside Custom System Roles, which is what makes "can view but not
 *  edit" common, so product turns it on with that; merged is not released.
 *  Created manually in the LD UI; defaults **off** in all four environments
 *  (local, development, staging, production) and is treated as off when absent. */
export const READ_ONLY_INDICATORS_FLAG = "read-only-indicators";

/** Gates the workspace-template SHARING vocabulary (NG-1355): the save dialog's
 *  "Shared with GTM" / "Private" choice, and the same two values on the templates
 *  table's tag and its Scope filter. A private template also loses its "Mark as
 *  default for new workspaces" action, which the backend refuses for one.
 *
 *  Off: exactly what shipped before it, "Global" / "Restricted", with
 *  "Restricted" posted as the deprecated alias the backend stores as `global`.
 *
 *  The dialog posts `private` with the flag on, which a backend older than
 *  NG-1355's sharing change refuses, so the flag must not go on in an
 *  environment until that backend is deployed there. Created manually in the
 *  LD UI on 2026-09-29, once that backend was on dev: **on** in development and
 *  staging, **off** in production and local, and treated as off when absent. */
export const WORKSPACE_TEMPLATE_SHARING_FLAG = "workspace-template-sharing";

/** Gates the Team Lead create affordances (NG-2176, under NG-1992): the create
 *  controls #5842's backend enabled but never surfaced — "New Project" on the
 *  Projects list and on a team's Projects tab, and "Create team" on My Teams —
 *  for callers who hold the create verb only at a led team's scope, not at the
 *  workspace scope.
 *
 *  Only the SHOWING of previously hidden controls rides this flag. The
 *  companion tightening — hiding controls whose write the backend refuses (a
 *  sub-team create for a non-lead, the "No parent" option for a caller without
 *  a workspace-level team create, the delivery-team options a caller cannot
 *  create under) — ships unflagged, the #5966 hide-only precedent: it removes
 *  a predictable authorization failure, not a capability.
 *
 *  The capability probes behind it (`project.create` / `team.create` with no
 *  `team_id`, answered from the workspace scope ∪ the caller's led teams) are
 *  ungated backend behaviour: they only ever feed an affordance, never
 *  authorize a write. Created manually in the LD UI; defaults **off** and is
 *  treated as off when the flag is absent. */
export const TEAM_LEAD_CREATE_FLAG = "team-lead-create";

/** Gates the Start of week control on Profile → Preferences (NG-2275). No view
 *  reads `Profile.start_of_the_week` yet, so turn it on only once they do.
 *  Hiding writes nothing, so users' saved choices survive. Created manually in
 *  the LD UI; defaults **off** and is treated as off when absent. */
export const START_OF_WEEK_FLAG = "start-of-week";

/** Gates the **Public holidays** card on Workspace Settings → Calendars
 *  (NG-2277). Inverted: the card had shipped, and off is what HIDES it, since
 *  Holiday calendars cover the same need. Only the UI is gated — the
 *  `holiday-calendar/me/` endpoint stays live, and a feed already generated keeps
 *  reducing capacity. Created manually in the LD UI; treated as off when absent. */
export const PUBLIC_HOLIDAYS_FLAG = "public-holidays";

/** Gates a person's own calendar connections (NG-2278): the "User calendar
 *  connections" card on workspace settings → Calendars, with its Connect Google /
 *  Connect Outlook buttons, sync and disconnect actions, and the toast shown on
 *  the OAuth redirect back. Ported from Plane and not validated, so it stays dark
 *  for the LA release. Holiday calendars and domain-wide sync are not behind it.
 *
 *  Only the UI is gated: the `users/me/calendar-*` endpoints stay live, and the
 *  30-minute OAuth sync keeps running for anyone already connected. Defaults
 *  **off** and is treated as off when absent. */
export const CALENDAR_CONNECTIONS_FLAG = "calendar-connections";

/** Gates the Home team designation on the project team pickers (NG-2161): the
 *  Create Project dialog (where Delivery teams also moves up to sit directly
 *  under Description), Project Settings › General, and the Project Overview
 *  properties panel. Each lists the selected teams under the picker with the
 *  Home team first, lets the user move Home with a "Set home" chip, locks the
 *  Home team in the picker so it can't be removed, and sends `home_team` with
 *  `delivery_teams`. The Overview panel header also shows the save while it is
 *  in flight, briefly as "Saved", or as "Save failed", and nothing at rest.
 *  The same key gates every Home-team surface: the program pickers (NG-2160),
 *  the Home team column and filter on the Projects and Programs lists
 *  (NG-2163), the report and dashboard surfaces (NG-2164), and the event "Home
 *  team" cards on Create Event, the event overview and the Events list's
 *  create dialog (NG-2155). Off, those event surfaces keep the plain project
 *  picker and send no `home_team_id`; the backend then keeps or derives the Home.
 *  On, every API request also carries `?home_scope=1` (`services/api.service.ts`),
 *  which lets members of a Home team's sub-teams read its projects and programs
 *  (NG-2174); the program Team Access copy says so.
 *
 *  Off: exactly the pickers that shipped before it, with no `home_team` in any
 *  payload. The backend then derives Home itself: it keeps the current Home
 *  while that team survives the new roster, else takes the first team of the
 *  new roster, and a legacy row with no Home derives its earliest delivery-team
 *  association. Needs the NG-2161 backend, which accepts `home_team`, deployed
 *  before it goes on in an environment. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent. */
export const HOME_TEAM_FLAG = "home-team";

/** Gates the SAML Single Logout copy on the SSO provider form (NG-2047): the
 *  SLO URL hint and the sharpened Logout URL hint that explain the SLO-vs-
 *  plain-redirect precedence. The SLO behaviour itself only activates when a
 *  deployment configures SP signing keys, but the copy is visible UI and so
 *  merges dark. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent. */
export const SAML_SINGLE_LOGOUT_COPY_FLAG = "saml-single-logout-copy";

/** Gates Workspace Settings → Integrations → Webhooks: the nav entry, the list
 *  page and the webhook detail page (NG-2330). The settings tab registry carries
 *  it, so the layout hides the entry and redirects a deep link, for both
 *  `webhooks` and `webhooks/:webhookId`.
 *
 *  This one hides a page that already shipped, rather than holding back a new
 *  one. Only work-item events are ever delivered (`work_items/handlers.py` is the
 *  sole producer), so four of the editor's five event checkboxes never fire
 *  (NG-591), and prod had no deliveries and no webhook edits in the 30 days to
 *  2026-10-02. The backend is not gated: the API stays, and existing webhooks
 *  keep receiving work-item events.
 *
 *  Created in LaunchDarkly **off** in every environment (Dan, 2026-10-02); the
 *  hermetic e2e stack forces it on so the page keeps its coverage. Product
 *  decides when it returns. Treated as off when absent, which hides the page. */
export const WEBHOOKS_FLAG = "webhooks-settings";

/** Gates the TanStack Query cache for the frontend caching pilot (NG-1043
 *  epic; foundation NG-1313, pilot NG-1314): project detail and its overview
 *  sub-resources, workspace teams and workspace members read through
 *  `app/queries/` instead of their MobX stores.
 *
 *  First read by NG-2543: `ProjectStore.loadProjectDetail`, which the project
 *  tab layout, overview and wrapper call. Created in LaunchDarkly **off**;
 *  treated as off when absent, which keeps every pilot read on the store it
 *  uses today. */
export const QUERY_CACHE_FLAG = "query-cache";

/** Gates the Pending Invitations tab on Workspace Settings → Members (NG-2490):
 *  the Members/Pending invitations tab switch, the dense invitations grid that
 *  replaces the stacked invitation cards, and the resend/edit/bulk actions on
 *  it. Off, the Members page is unchanged — the existing stacked-cards panel
 *  still renders beneath the Members grid. Created manually in the LD UI;
 *  defaults **off** and is treated as off when absent. */
export const PENDING_INVITATIONS_TAB_FLAG = "pending-invitations-tab";
